import { useId, useState, type ReactElement, type ReactNode, type SVGProps } from 'react';
import { cn } from '@immo/ui-kit';
import { ChevronDownIcon, ChevronRightIcon, PlusIcon } from '@/components/layout/icons';

type SectionProps = {
  readonly title: string;
  readonly icon: (props: SVGProps<SVGSVGElement>) => ReactElement;
  readonly subtitle?: ReactNode;
  readonly addable?: boolean;
  readonly collapsible?: boolean;
  readonly collapsedSummary?: ReactNode;
  readonly children: ReactNode;
};

export function Section({
  title,
  icon: Icon,
  subtitle,
  addable = false,
  collapsible = false,
  collapsedSummary,
  children,
}: SectionProps) {
  const [open, setOpen] = useState(true);
  const bodyId = useId();
  const Chevron = open ? ChevronDownIcon : ChevronRightIcon;
  const addButton = addable && (
    <button
      type="button"
      className="inline-flex h-6 w-6 items-center justify-center rounded-md text-text-tertiary transition-colors hover:bg-hover-bg hover:text-text-primary"
      aria-label="add"
    >
      <PlusIcon className="h-4 w-4" />
    </button>
  );

  if (collapsible) {
    return (
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="min-w-0 flex-1 text-base-sm font-semibold text-text-secondary">
            <button
              type="button"
              aria-expanded={open}
              aria-controls={bodyId}
              onClick={() => setOpen((value) => !value)}
              className="flex w-full flex-wrap items-baseline gap-x-2 gap-y-1 rounded-md text-left hover:text-text-primary"
            >
              <Chevron className="h-3.5 w-3.5 shrink-0 self-center text-text-tertiary" aria-hidden />
              <Icon className="h-4 w-4 self-center text-text-tertiary" aria-hidden />
              {title}
              {subtitle !== undefined && (
                <span className="text-base-sm font-normal text-text-tertiary">{subtitle}</span>
              )}
              {!open && collapsedSummary !== undefined && (
                // 46px = chevron 14 + gap 8 + icon 16 + gap 8, so line 2 sits under the title.
                <span className="basis-full pl-[46px] font-normal text-text-tertiary">{collapsedSummary}</span>
              )}
            </button>
          </h2>
          {addButton}
        </div>
        <div id={bodyId} hidden={!open}>
          {children}
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="inline-flex items-baseline gap-2 text-base-sm font-semibold text-text-secondary">
          <Icon className="h-4 w-4 self-center text-text-tertiary" aria-hidden />
          {title}
          {subtitle !== undefined && <span className="text-base-sm font-normal text-text-tertiary">{subtitle}</span>}
        </h2>
        {addButton}
      </div>
      {children}
    </section>
  );
}

type SidebarSectionProps = {
  readonly title: string;
  readonly children: ReactNode;
};

export function SidebarSection({ title, children }: SidebarSectionProps) {
  return (
    <section className="space-y-3">
      <h3 className="inline-flex items-center gap-1.5 text-base-sm font-semibold text-text-secondary">
        <ChevronDownIcon className="h-3.5 w-3.5 text-text-tertiary" aria-hidden />
        {title}
      </h3>
      {children}
    </section>
  );
}

type DetailRowProps = {
  readonly icon?: (props: SVGProps<SVGSVGElement>) => ReactElement;
  readonly label: ReactNode;
  readonly value: ReactNode;
  readonly className?: string;
};

export function DetailRow({ icon: Icon, label, value, className }: DetailRowProps) {
  return (
    <div className={cn('grid grid-cols-[1fr_1fr] items-baseline gap-3', className)}>
      <dt className="inline-flex min-w-0 items-center gap-1.5 text-text-tertiary">
        {Icon !== undefined && <Icon className="h-3.5 w-3.5" aria-hidden />}
        {label}
      </dt>
      <dd className="text-text-primary">{value}</dd>
    </div>
  );
}
